import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import showToast from "../components/Toast";

// Same manual-header pattern as userApiCalls.js — there is no axios
// interceptor in this app, by design.
const storedUser = () => JSON.parse(localStorage.getItem("user")) || null;

const authHeader = () => {
  const user = storedUser();
  if (!user) throw new Error("Not logged in.");
  return { authorization: "Bearer " + user.token };
};

const errorMessage = (error, fallback) =>
  error.response?.data?.error || fallback;

// ------------------------- candidate search -----------------------------
export const getClaimCandidates = async (name) => {
  const response = await axios.get("/api/v1/user/me/claim-candidates", {
    params: { name },
    headers: authHeader(),
  });
  // The truncation flag travels beside the list, so the shape here is an
  // object rather than the bare array the other hooks return.
  return {
    players: response.data.data,
    truncated: Boolean(response.data.truncated),
  };
};

export const useClaimCandidates = (name) =>
  useQuery(["claim-candidates", name], () => getClaimCandidates(name), {
    // The API requires two characters; don't fire a request it will reject.
    enabled: (name || "").trim().length >= 2,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

// ---------------------------- own claims --------------------------------
export const getMyClaims = async () => {
  const response = await axios.get("/api/v1/user/me/claims", {
    headers: authHeader(),
  });
  return response.data.data;
};

export const useMyClaims = () =>
  useQuery("my-claims", getMyClaims, { refetchOnWindowFocus: false });

// ---------------------------- create claim ------------------------------
export const createClaim = async (claim) => {
  const response = await axios.post("/api/v1/user/me/claims", claim, {
    headers: authHeader(),
  });
  return response.data.data;
};

export const useCreateClaim = () => {
  const queryClient = useQueryClient();

  return useMutation(createClaim, {
    onSuccess: () => {
      queryClient.invalidateQueries("my-claims");
      showToast("Claim submitted. An admin will review it.", "success");
    },
    onError: (error) =>
      showToast(errorMessage(error, "Couldn't submit that claim."), "error"),
  });
};

// -------------------------- document upload -----------------------------
export const uploadClaimDocument = async ({ id, file }) => {
  const form = new FormData();
  form.append("document", file);

  const response = await axios.put(
    `/api/v1/user/me/claims/${id}/document`,
    form,
    { headers: authHeader() }
  );
  return response.data.data;
};

export const useUploadClaimDocument = () => {
  const queryClient = useQueryClient();

  return useMutation(uploadClaimDocument, {
    onSuccess: () => {
      queryClient.invalidateQueries("my-claims");
      showToast("Document attached.", "success");
    },
    onError: (error) =>
      showToast(errorMessage(error, "Couldn't upload that document."), "error"),
  });
};

// ---------------------------- cancel claim ------------------------------
export const cancelClaim = async (id) => {
  const response = await axios.delete(`/api/v1/user/me/claims/${id}`, {
    headers: authHeader(),
  });
  return response.data.data;
};

export const useCancelClaim = () => {
  const queryClient = useQueryClient();

  return useMutation(cancelClaim, {
    onSuccess: () => {
      queryClient.invalidateQueries("my-claims");
      showToast("Claim withdrawn.", "success");
    },
    onError: (error) =>
      showToast(errorMessage(error, "Couldn't withdraw that claim."), "error"),
  });
};
